/**
 * The authorization chokepoint — the single place that answers "may this person do X?".
 *
 * A `decide()`/`authorize()` split, per `docs/roadmap.md` §3.2 and backlog T4: the *decision* is a
 * pure function over plain values, so every rule below is covered by `authorization.test.ts` with
 * no database; the thin wrapper that fetches a session's real role and hands it to `decide` lives
 * in `server/` (Phase 1) and must stay thin rather than growing decision logic back into itself.
 *
 * The role model is clinical-specific by design (backlog T5): a coarse `org_admin` / `member` /
 * `billing_admin` split is far too coarse for a clinical setting — the difference between a
 * prescriber and a medical assistant is a regulatory boundary, not a permissions preference, and a
 * single "member" role would collapse six clinical and administrative jobs into one.
 *
 * **Authorization is not entitlement.** This module asks "may this person do this?" and fails with
 * 403. `entitlements.ts` asks "does this practice's plan include this?" and fails with a distinct,
 * upgrade-shaped error. They are separate functions, separate middlewares, and separate audit
 * events even though both gate routes — and the failures must not be conflated in either
 * direction: **a user who lacks a capability by role must never be told to upgrade**, because the
 * upgrade would not grant it and the message misdirects a practice into paying for the wrong fix.
 * The converse matters too: a practice that has not licensed a feature must not see "you do not
 * have permission", which sends an administrator hunting for a role change that does not exist.
 * See `docs/decisions/tiers-and-entitlements.md`.
 *
 * **Authorization is also not minimum-necessary access.** Holding `chart:read` says this role may
 * read clinical content *somewhere*; it says nothing about whether it may read *this patient's*
 * chart. That second question is `careTeam.ts` (backlog T6), which composes this module with
 * `confidentiality.ts`. Being a member of the right practice must not by itself open any chart, so
 * no route may treat a `decide` result as sufficient for a PHI read.
 *
 * Fails closed throughout: an unrecognised role, an absent role, and a capability no role holds are
 * all denials, never defaults-to-allow — `claude-kit/rules/invariants.md`, "Deny by default on any
 * auth surface" and "Centralize the actual authorization decision, not just the default".
 */

/**
 * Roles held inside one practice (one tenant). Exactly one per membership.
 *
 * A person who genuinely does two jobs — the office manager who is also a medical assistant — holds
 * two memberships, rather than this union growing a combined role. That keeps every row of the
 * capability table below defensible on its own, and keeps "why could this person see that?"
 * answerable from one row rather than from a hand-merged union of several.
 *
 * The clinical staff distinctions are load-bearing rather than cosmetic:
 *
 *   - `physician` and `advanced_practice_provider` (NP/PA) are the prescriber roles. They are kept
 *     separate because co-signature supervision differs between them, and because state scope-of-
 *     practice rules differ for the second (`emr-po` A3 covers e-prescribing scope) — collapsing
 *     them would make that difference unrepresentable.
 *   - `medical_assistant` is not a narrower nurse. It is a role that must *never* prescribe; see
 *     `PRESCRIBER_ROLES`.
 *   - `referring_provider` is external to the practice. It exists so a referral can be answered
 *     without granting practice-wide access, and it is read-only.
 *   - `auditor` is the compliance/privacy-officer role and is read-only, enforced by test rather
 *     than by convention.
 *
 * Extend deliberately. A role added here without a row in `CAPABILITIES_BY_ROLE` fails typecheck,
 * which is the intended forcing function.
 */
export const ROLES = [
  /** Practice administrator / office manager. Administrative authority, no clinical content. */
  'practice_admin',
  /** Physician (MD/DO). Prescriber, signer, co-signer. */
  'physician',
  /** Nurse practitioner or physician assistant. Prescriber and signer; does not co-sign others. */
  'advanced_practice_provider',
  /** Clinical staff — RN/LPN. Documents and reads the chart; does not prescribe. */
  'nurse',
  /** Medical assistant. Rooming, vitals, chart documentation within scope. Never prescribes. */
  'medical_assistant',
  /** Front desk / scheduling. Demographics, coverage, the calendar. No clinical content. */
  'front_desk',
  /** Billing / RCM staff. Coverage and claims. No clinical content. */
  'billing',
  /** External referring provider. Read-only, and only for patients they are on the care team of. */
  'referring_provider',
  /** Compliance officer / auditor. Read-only, audit log only — see `READ_ONLY_ROLES`. */
  'auditor',
  /**
   * A membership held in a *parent* org (an MSO or billing company, `orgs.parentOrgId` —
   * `BACKLOG.md` T14/T15/T16). Distinct from `practice_admin` on purpose, per T16: an MSO's
   * billing staff acting across many practices is not the same principal as a practice's own
   * billing staff, and conflating them would put one practice's data in another's reach the
   * moment `practice_admin` gained any cross-org visibility. Read-only (`READ_ONLY_ROLES`) and
   * administrative basis only, never a treatment relationship — see `chart:read`'s absence from
   * this role's row in `CAPABILITIES_BY_ROLE` and `careTeam.ts`'s
   * `TREATMENT_RELATIONSHIPS_BY_ROLE`, which holds `[]` for it, matching `practice_admin`.
   * Holding this role in an org with no children is harmless (see `org:read_children`'s own
   * comment) — nothing enforces that this role is only assigned in a parent org, which is a
   * known, accepted simplification for this first pass rather than an oversight.
   */
  'reseller_admin',
] as const;

export type Role = (typeof ROLES)[number];

/**
 * Capabilities. Deliberately small: this is the mechanism proven against what Phase 1 (the HIPAA
 * floor) and Phase 3 (clinical core) actually need, and it is **extended as real routes are built
 * rather than pre-populated speculatively** — a capability nobody checks looks enforced and is not,
 * which is the same failure `confidentiality.ts` warns about for unused restriction reasons.
 *
 * Two read capabilities rather than one, because the split is what makes minimum-necessary
 * expressible at all:
 *
 *   - `patient:read` is the administrative surface — that the patient exists, demographics,
 *     coverage, contact details. Front desk and billing cannot do their jobs without it.
 *   - `chart:read` is clinical content — notes, problems, medications, results.
 *
 * Collapsing the two would force a choice between handing the front desk the entire chart and
 * making it impossible to book an appointment. `careTeam.ts` turns this pair into the `clinical`
 * vs `administrative` access scope.
 */
export const ORG_CAPABILITIES = [
  /** Patient exists, demographics, coverage, contact details. Not clinical content. */
  'patient:read',
  /** Edit demographics and coverage. */
  'patient:write',
  /** Clinical content: notes, problem list, medications, allergies, results. */
  'chart:read',
  /** Document clinical content. */
  'chart:write',
  /** Create or renew a prescription. Restricted to `PRESCRIBER_ROLES` — see that constant. */
  'chart:prescribe',
  /** Attest to and sign one's own clinical note. */
  'note:sign',
  /** Co-sign another clinician's note (supervisory attestation). */
  'note:cosign',
  /** Book, move, and cancel appointments. */
  'schedule:manage',
  /** Charge capture, claims, ERA posting, patient statements. */
  'billing:manage',
  /** Invite, suspend, and re-role members of this practice. */
  'org:manage_members',
  /** Practice-level settings: locations, templates, branding, integrations. */
  'org:manage_settings',
  /** Read this practice's own audit log, including PHI-read events (§164.312(b)). */
  'audit:read',
  /**
   * Take break-glass access to a chart this person has no treatment relationship with, or that
   * carries a confidentiality restriction they are not permitted (backlog T7).
   *
   * A capability rather than an ambient possibility, so that "who may break glass" is answerable
   * from the table below. Held only by roles that already hold `chart:read` — a role that may not
   * read a chart under any circumstances must not acquire that ability by asserting an emergency.
   * That containment is a test (`break-glass is only held by roles that can already read a chart`),
   * not a comment.
   */
  'chart:break_glass',
  /**
   * Release chart content to a named recipient outside this practice's own treatment team, under a
   * CMIA authorization or a statutory no-authorization-required basis (`BACKLOG.md` C14,
   * `docs/decisions/cmia-disclosure-scope.md`). Distinct from `chart:read` the same way `patient:
   * read` and `chart:read` are already split — a disclosure is not "viewing content on screen for
   * treatment," it is content leaving the practice's boundary to a third party, a materially
   * higher-risk action deserving its own gate the same way `chart:break_glass` gets its own
   * capability rather than riding on `chart:read` alone. A write, not a read — see
   * `READ_ONLY_CAPABILITIES` below, mirroring `chart:break_glass`'s own reasoning.
   */
  'chart:disclose',
  /**
   * May a parent org's session expand its RLS-visible row set to include its direct child orgs'
   * rows (`BACKLOG.md` T14/T15/T16)? Read-only by construction (`READ_ONLY_CAPABILITIES` below) —
   * this capability only ever widens what a `SELECT` may see, never what a write may reach; every
   * `WITH CHECK` a request's reachable-org-set touches is untouched by this capability, which is
   * the hazard T15 names as "read-up and write-down are different grants".
   *
   * Held only by `reseller_admin`. Deliberately not folded into `practice_admin`: an ordinary
   * practice administrator whose org happens to later gain a child (or be re-parented) must not
   * silently gain cross-org visibility just because the org structure changed underneath them —
   * the capability is what makes that a deliberate role decision instead of an emergent one.
   */
  'org:read_children',
] as const;

/**
 * Platform-operator capabilities — our own staff, not the practice's. Held in a set entirely
 * separate from every org role, so that no sequence of org role changes inside a tenant can reach
 * one.
 *
 * **No platform capability grants PHI access, and no platform operator holds an org capability.**
 * A vendor employee is not a member of the care team; support work that genuinely requires reading
 * a chart goes through a practice-granted membership and the break-glass path, so it produces the
 * same loud audit event as any other out-of-band access rather than being invisible under a
 * support role. Both halves are tested.
 */
export const PLATFORM_CAPABILITIES = [
  /** Provision, suspend, and configure practices (tenants) at the platform level. */
  'platform:manage_practices',
  /** Read the platform's own operational audit log. Never a practice's PHI-read events. */
  'platform:read_audit_log',
] as const;

export const CAPABILITIES = [...ORG_CAPABILITIES, ...PLATFORM_CAPABILITIES] as const;
export type Capability = (typeof CAPABILITIES)[number];

/**
 * Capabilities that only observe. Everything not listed here changes state, orders something,
 * attests to something, or widens someone's access.
 *
 * This exists so that "role X is read-only" can be enforced by iterating every capability rather
 * than by spot-checking a list that goes stale the moment a capability is added. Adding a new
 * capability therefore requires a deliberate decision about whether it belongs here, and until
 * that decision is made the safe default holds: it counts as a write, and no read-only role gets
 * it.
 *
 * `chart:break_glass` is deliberately absent. It reads rather than writes clinical data, but it
 * grants access that was otherwise denied and fires a loud audit event, so treating it as a write
 * is what keeps it away from the read-only roles.
 */
export const READ_ONLY_CAPABILITIES = [
  'patient:read',
  'chart:read',
  'audit:read',
  'platform:read_audit_log',
  'org:read_children',
] as const;

/**
 * Roles that must hold no write capability at all.
 *
 *   - `auditor` — compliance reviews *access*, it does not alter records. An auditor with any write
 *     capability is a separation-of-duties failure: the person who reviews the audit trail must not
 *     be able to author the events in it.
 *   - `referring_provider` — external. A provider outside the practice may read what they were sent
 *     for; they document in their own system, not in ours.
 *   - `reseller_admin` — an MSO/billing-company principal (`BACKLOG.md` T14/T15/T16). Administrative
 *     basis across managed practices, never a treatment relationship, and never a write into a
 *     child org — see `org:read_children`'s own comment.
 */
export const READ_ONLY_ROLES = ['auditor', 'referring_provider', 'reseller_admin'] as const;

/**
 * The only roles that may prescribe.
 *
 * **This is a regulatory and patient-safety boundary, not a product choice.** Prescriptive
 * authority is conferred by state licensure and (for controlled substances) DEA registration on
 * the individual practitioner. A medical assistant, a nurse, front-desk staff, or an administrator
 * holding a prescribe capability would not be a permissions bug to tidy up later — it would be the
 * software enabling the unlicensed practice of medicine, and for controlled substances it would
 * also breach the EPCS identity-proofing and two-factor signing requirements that bind the
 * prescriber personally (21 CFR Part 1311).
 *
 * `nurse` is excluded on purpose despite being a licensed clinical role: an RN transcribing or
 * queueing a prescriber's order is a different action from *being* the prescriber, and if that
 * workflow is needed it earns its own capability rather than widening this set.
 *
 * The test named for this boundary iterates every role, so a future edit to
 * `CAPABILITIES_BY_ROLE` that grants `chart:prescribe` anywhere else fails the build.
 */
export const PRESCRIBER_ROLES = ['physician', 'advanced_practice_provider'] as const;

/**
 * The capability table. Data, not branching logic, so that "what can this role do?" is one row and
 * a review of a permissions change is a diff of a table.
 *
 * `Record<Role, ...>` rather than a partial map: a role added to `ROLES` without a row here is a
 * typecheck failure, not a silent empty set that reads as "denied everything" until someone
 * notices a whole job function cannot log in.
 *
 * Two rows are worth reading twice, because both look like omissions and are not:
 *
 *   - **`practice_admin` holds no clinical capability.** Administrative authority over a practice
 *     is not a treatment relationship, and the roadmap's minimum-necessary requirement
 *     (`docs/roadmap.md` §3.2) is not satisfied by a role that can read every chart because it can
 *     also manage members. An office manager who needs clinical access holds a clinical membership
 *     too — see `ROLES`.
 *   - **`auditor` holds `audit:read` and nothing else, not even `patient:read`.** Compliance audits
 *     *access to* records rather than their content; the audit log itself carries who read what and
 *     under which basis (`docs/roadmap.md` §3.2), and the confidentiality label of each event
 *     (backlog U1), which is what a privacy investigation actually needs. If a specific
 *     investigation requires the content, it goes through a clinician's break-glass, producing its
 *     own audited event, rather than through a standing capability that would let the reviewer read
 *     any chart in the practice unremarked.
 */
const CAPABILITIES_BY_ROLE: Record<Role, readonly Capability[]> = {
  practice_admin: [
    'patient:read',
    'patient:write',
    'schedule:manage',
    'org:manage_members',
    'org:manage_settings',
    'audit:read',
    // BACKLOG.md C14 — resolves C22's own named gap (chart-export's "no front-desk/billing-
    // initiated release-of-information workflow" finding) plus the practice's own compliance
    // function. See `chart:disclose`'s own comment above for why this is a write, not `chart:read`.
    'chart:disclose',
  ],
  physician: [
    'patient:read',
    'patient:write',
    'chart:read',
    'chart:write',
    'chart:prescribe',
    'note:sign',
    'note:cosign',
    'schedule:manage',
    'chart:break_glass',
  ],
  advanced_practice_provider: [
    'patient:read',
    'patient:write',
    'chart:read',
    'chart:write',
    'chart:prescribe',
    'note:sign',
    'schedule:manage',
    'chart:break_glass',
  ],
  nurse: [
    'patient:read',
    'patient:write',
    'chart:read',
    'chart:write',
    'note:sign',
    'schedule:manage',
    'chart:break_glass',
  ],
  // No `chart:break_glass`: an MA works at the direction of a clinician, so the emergency-override
  // decision belongs to that clinician, who is the one accountable for it in the audit trail.
  medical_assistant: [
    'patient:read',
    'patient:write',
    'chart:read',
    'chart:write',
    'schedule:manage',
  ],
  // `chart:disclose` (BACKLOG.md C14) — the front-desk persona `C22`'s own text names ("to fax a
  // chart out"). See `chart:disclose`'s own comment above.
  front_desk: ['patient:read', 'patient:write', 'schedule:manage', 'chart:disclose'],
  // `patient:write` because correcting coverage and payer details is billing's own work, and a
  // billing clerk blocked from fixing a subscriber ID files a support ticket for every claim.
  // `chart:disclose` (BACKLOG.md C14) — the billing-initiated release-of-information persona `C22`'s
  // own text names alongside front desk. See `chart:disclose`'s own comment above.
  billing: ['patient:read', 'patient:write', 'billing:manage', 'chart:disclose'],
  referring_provider: ['patient:read', 'chart:read'],
  auditor: ['audit:read'],
  // Administrative surface only (`patient:read`), across managed practices (`org:read_children`) —
  // never `chart:read`, matching T16's "administrative basis, never a treatment relationship".
  reseller_admin: ['patient:read', 'org:read_children'],
};

function includes(list: readonly string[], value: string): boolean {
  return list.includes(value);
}

/**
 * Is `value` a role this system recognises? Takes `string | null` because the caller's input is a
 * membership row that may be absent, and "no membership" and "a role name we do not recognise"
 * must land on the same denial rather than one of them throwing.
 */
export function isRole(value: string | null): value is Role {
  return value !== null && includes(ROLES, value);
}

export function isCapability(value: string): value is Capability {
  return includes(CAPABILITIES, value);
}

/** Is this capability one that only observes? See `READ_ONLY_CAPABILITIES`. */
export function isReadOnlyCapability(capability: Capability): boolean {
  return includes(READ_ONLY_CAPABILITIES, capability);
}

/**
 * Does this capability change state, order something, attest to something, or widen access?
 *
 * Defined as the complement of `READ_ONLY_CAPABILITIES` rather than as its own list, so a newly
 * added capability counts as a write until someone deliberately says otherwise. Two independent
 * lists would let a capability be absent from both and slip past the read-only-role test.
 */
export function isWriteCapability(capability: Capability): boolean {
  return !isReadOnlyCapability(capability);
}

/** What a session's acting identity actually holds. Resolved by the server wrapper, never by this
 * module — and never from the request body (`claude-kit/rules/invariants.md`,
 * "Server-authoritative values never come from the request"). */
export interface Grants {
  /** Our own staff, resolved from a platform grant table — never from an org membership. */
  readonly isPlatformOperator: boolean;
  /** The acting identity's role in the practice being acted on. `null` when they hold none. */
  readonly role: string | null;
}

/**
 * Pure: given what a session's acting identity holds, is `capability` granted?
 *
 * Fails closed in three separate ways, all of them tested:
 *
 *   1. A platform capability is granted only to a platform operator, whatever org role is present.
 *   2. An org capability is granted only from the role table, and a platform operator gets nothing
 *      from it — vendor staff are not members of the care team.
 *   3. An unrecognised or absent role holds nothing. A role name arriving here that this build does
 *      not recognise means corrupt data or a half-finished migration, and neither should open a
 *      chart.
 *
 * `capability` is the closed union rather than a `string` because every call site names it in code;
 * `role` is a `string` because it arrives from the database, which is the same split
 * `entitlements.ts`'s `isEntitled(tier: string, feature: Feature)` makes for the same reason.
 */
export function decide(grants: Grants, capability: Capability): boolean {
  if (includes(PLATFORM_CAPABILITIES, capability)) {
    return grants.isPlatformOperator;
  }
  if (!isRole(grants.role)) {
    return false;
  }
  return includes(CAPABILITIES_BY_ROLE[grants.role], capability);
}

/**
 * Every capability a role holds. Exported for the UI, which needs to hide what it must not offer,
 * and for the members-management screen, which has to explain what a role means before an
 * administrator assigns it.
 *
 * Returns `[]` for an unrecognised role, matching `decide`'s own fail-closed behaviour rather than
 * throwing — a corrupt membership row should render an empty screen, not a 500.
 */
export function capabilitiesForRole(role: string | null): readonly Capability[] {
  if (!isRole(role)) return [];
  return CAPABILITIES_BY_ROLE[role];
}
